// Constants
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { createIdentitySet } from "../../../utilities/identity";
import { ContentLinkExtensionKeys } from "../../../service/planner/extensions/IContentLinkExtensionsResource";
import { ContentLinkType, HostedAppReferenceLegacyType } from "../../../service/planner/enumerations/IPlannerEnumerations";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import cloneDeep from "lodash/cloneDeep";
import { getLinkTypeFromUrl } from "../../../utilities/FileExtensionUtilities";
/**
 * The content link entity implementation.
 */ export class ContentLink extends PatchableAndDeltaSyncableODataEntity {
    get previewPriority() {
        return this.extensions.previewPriority ?? null;
    }
    /**
     * Initialize extensions with auto-detection of type if not provided
     */ initializeExtensions(propertyBag) {
        const extensions = propertyBag.extensions ?? {};
        // Skip sanity check for To Do tasks as they do not have well formed URL's
        if (extensions.todoDetails) {
            return extensions;
        }
        // Auto-detect type for WebReference and DocumentReference if not provided
        if (!extensions.resourceClassificationType) {
            if (this.linkType === ContentLinkType.WebReference || this.linkType === ContentLinkType.DocumentReference) {
                const linkTypeInfo = getLinkTypeFromUrl(propertyBag.webUrl);
                return {
                    ...extensions,
                    resourceClassificationType: linkTypeInfo.resourceClassificationType
                };
            }
            if (this.linkType === ContentLinkType.HostedApp) {
                return {
                    ...extensions,
                    resourceClassificationType: HostedAppReferenceLegacyType.TeamsHostedApp
                };
            }
        }
        return extensions;
    }
    mapEntityChangesToResource(changed, source) {
        const resourceKeyMap = this.getChangePropertyMapping();
        return {
            [resourceKeyMap.displayName]: changed.displayName,
            [resourceKeyMap.extensions]: changed.extensions?.previewPriority ? this.serializeExtensionsForUpdate() : undefined
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            createdBy: (deltaValue)=>deltaValue,
            createdDateTime: (deltaValue)=>Temporal.Instant.from(deltaValue),
            webUrl: (deltaValue)=>{
                try {
                    return decodeURIComponent(deltaValue);
                } catch (decodeError) {
                    throw new Error(`Deserialization Error: [Entity=ContentLink][Field=webUrl] Failed to decode web url`);
                }
            },
            extensions: (deltaValue)=>ContentLink.deserializeExtensions(deltaValue)
        });
    }
    static fromResource(resource) {
        let decodedWebUrl;
        try {
            decodedWebUrl = decodeURIComponent(resource.webUrl);
        } catch (decodeError) {
            throw new Error(`Deserialization Error: [Entity=ContentLink][Field=webUrl] Failed to decode web url`);
        }
        return new ContentLink({
            id: resource.id,
            version: resource["@etag"],
            displayName: resource.displayName,
            linkType: resource.linkType,
            createdBy: createIdentitySet(resource.createdBy),
            // Temporary fix for createdDateTime being null from the service
            createdDateTime: resource.createdDateTime ? Temporal.Instant.from(resource.createdDateTime) : undefined,
            webUrl: decodedWebUrl,
            lastModifiedBy: createIdentitySet(resource.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            extensions: ContentLink.deserializeExtensions(resource.extensions)
        });
    }
    static encodeWebUrl(webUrl) {
        return webUrl.replace(/%/g, "%25").replace(/\./g, "%2E").replace(/:/g, "%3A").replace(/@/g, "%40").replace(/#/g, "%23");
    }
    /**
     * Deserializes the extensions of the content link.
     * The values must be serializable to JSON.
     */ static deserializeExtensions(extensions) {
        let result = {};
        const isPLinkEnabled = AppContext?.appConfiguration?.flights?.EnableEvoPLink === true;
        // First parse the legacy data if it exists
        const serializedLegacyData = extensions[ContentLinkExtensionKeys.legacyData];
        if (serializedLegacyData) {
            try {
                const legacyExtensionData = JSON.parse(serializedLegacyData);
                if (legacyExtensionData) {
                    result = {
                        ...result,
                        previewPriority: legacyExtensionData.previewPriority,
                        // Preserve type as string if it exists, even if it's not a known enum value
                        resourceClassificationType: legacyExtensionData.type
                    };
                }
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=ContentLink][Field=Extensions][Key=legacyData]`);
            }
        }
        // TODO: Remove client-side PLink extension handling once service work is checked in.
        // PLink data currently lives under its own top-level key; see ContentLinkExtensionKeys.plinkData.
        if (isPLinkEnabled) {
            const serializedPlinkData = extensions[ContentLinkExtensionKeys.plinkData];
            if (serializedPlinkData) {
                try {
                    const plinkExtensionData = JSON.parse(serializedPlinkData);
                    if (plinkExtensionData) {
                        result = {
                            ...result,
                            plinkType: plinkExtensionData.plinkType
                        };
                    }
                } catch (parseError) {
                    throw new Error(`Deserialization Error: [Entity=ContentLink][Field=Extensions][Key=plinkData]`);
                }
            }
        }
        return result;
    }
    getClone() {
        return new ContentLink({
            id: this.id,
            version: this.version,
            linkType: this.linkType,
            createdBy: cloneDeep(this.createdBy),
            createdDateTime: Temporal.Instant.from(this.createdDateTime),
            webUrl: this.webUrl,
            displayName: this.displayName,
            lastModifiedBy: cloneDeep(this.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(this.lastModifiedDateTime),
            extensions: cloneDeep(this.extensions)
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            ...this.forCreate(),
            id: this.id,
            "@etag": this.version,
            displayName: this.displayName,
            createdBy: this.createdBy,
            createdDateTime: this.createdDateTime.toString(),
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime.toString(),
            extensions: this.serializeExtensions()
        };
    }
    forCreate() {
        return {
            displayName: this.displayName,
            linkType: this.linkType,
            webUrl: ContentLink.encodeWebUrl(this.webUrl),
            extensions: this.serializeExtensions()
        };
    }
    /**
     * Serialize the extensions for updates (only previewPriority, not type).
     * The values must be serializable to JSON.
     */ serializeExtensionsForUpdate() {
        const output = {};
        if (this.linkType === ContentLinkType.WebReference || this.linkType === ContentLinkType.DocumentReference) {
            const legacyData = {
                previewPriority: this.extensions.previewPriority
            };
            output[ContentLinkExtensionKeys.legacyData] = JSON.stringify(legacyData);
        }
        return output;
    }
    /**
     * Serialize the extensions.
     * The values must be serializable to JSON.
     */ serializeExtensions() {
        const output = {};
        const isPLinkEnabled = AppContext?.appConfiguration?.flights?.EnableEvoPLink === true;
        if (this.linkType === ContentLinkType.WebReference || this.linkType === ContentLinkType.DocumentReference || this.linkType === ContentLinkType.HostedApp) {
            const legacyData = {
                previewPriority: this.extensions.previewPriority,
                type: this.extensions.resourceClassificationType
            };
            output[ContentLinkExtensionKeys.legacyData] = JSON.stringify(legacyData);
        }
        // TODO: Remove client-side PLink extension handling once service work is checked in.
        // The service currently drops unknown keys nested inside LegacyData, so PLink data uses its own top-level key.
        if (isPLinkEnabled && this.extensions.plinkType) {
            const plinkData = {
                plinkType: this.extensions.plinkType
            };
            output[ContentLinkExtensionKeys.plinkData] = JSON.stringify(plinkData);
        }
        return output;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.displayName = propertyBag.displayName;
        this.linkType = propertyBag.linkType;
        this.createdBy = propertyBag.createdBy ?? propertyBag.lastModifiedBy;
        this.createdDateTime = propertyBag.createdDateTime ?? Temporal.Now.instant();
        this.webUrl = propertyBag.webUrl;
        this.lastModifiedBy = propertyBag.lastModifiedBy;
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime ?? Temporal.Now.instant();
        this.extensions = this.initializeExtensions(propertyBag);
    }
}
